/**
 * Full-width banner for the one subscription state loud enough to deserve it:
 * moderation has actually stopped.
 *
 * Everything milder (healthy trial, 80%/90% warnings, trial not yet started) is
 * a compact chip in the top bar next to the user name — see <SubscriptionChip>.
 * A persistent strip saying "you're fine" was just noise above every screen.
 *
 * Deliberately a banner rather than a toast: the app mounts no Radix <Toaster>,
 * so useToast() renders nothing. A banner is also the right medium — "moderation
 * has stopped" is a standing condition, not a transient event.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { Ban } from "lucide-react";
import { Button } from "./ui/button";
import Progress from "./ui/progress";
import { useSubscription } from "../contexts/SubscriptionContext";
import { formatNumber, getNextPlan } from "../config/plans";
import { usePlanCatalog } from "../hooks/usePlanCatalog";

const SubscriptionBanner: React.FC = () => {
  const navigate = useNavigate();
  const { subscription, loading } = useSubscription();
  const { catalog } = usePlanCatalog();

  if (loading || !subscription) return null;

  const { usage, status, trialStarted, moderationPaused, moderationPausedReason } =
    subscription;

  // Anything short of "we have stopped moderating" is handled by the chip.
  if (!moderationPaused) return null;

  const comments = usage.comments;

  const message =
    moderationPausedReason === "quota"
      ? `Moderation has stopped — you've used all ${formatNumber(comments.limit ?? 0)} comments for this period. New comments are still collected, but nothing is being moderated.`
      : status === "expired" && !trialStarted
        ? "Your subscription is not active. Moderation has stopped."
        : "Your plan has ended. Moderation has stopped — new comments are still collected, but nothing is being moderated.";

  // Name the target tier so the CTA says what it actually does. Someone already
  // on Custom has nothing above them, so send them to a conversation instead.
  const nextPlan = getNextPlan(catalog, subscription.plan);
  const cta = nextPlan
    ? { label: `Upgrade to ${nextPlan.name}`, to: "/pricing" }
    : { label: "Contact us", to: "/contact" };

  return (
    <div className="border-b px-4 sm:px-6 py-3 bg-red-50 border-red-200">
      <div className="flex flex-wrap items-center gap-3">
        <Ban className="h-5 w-5 shrink-0 text-red-800" />
        <p className="text-sm flex-1 min-w-[16rem] text-red-800">{message}</p>

        {comments.limit !== null && (
          <Progress
            value={comments.used}
            max={comments.limit}
            className="w-full sm:w-56"
            size="sm"
            label="Comments used"
            valueText={`${formatNumber(comments.used)} of ${formatNumber(comments.limit)} comments used`}
          />
        )}

        <Button size="sm" variant="outline" onClick={() => navigate(cta.to)}>
          {cta.label}
        </Button>
      </div>
    </div>
  );
};

export default SubscriptionBanner;
